import { useEffect, useState } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Save, RotateCcw, IndianRupee, MapPin, Tag, Shield, Percent, Package, Clock, Truck } from "lucide-react";
import { toast } from "sonner";

function Section({ title, icon: Icon, children, testId }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid={testId}>
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
          <Icon className="h-4 w-4 text-[#FF6B00]" />
        </div>
        <h2 className="font-['Outfit'] text-base font-semibold text-slate-900">{title}</h2>
      </div>
      {children}
    </div>
  );
}

export default function AdminB2BPricing() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchConfig = async () => {
    try {
      const { data } = await api.get("/admin/b2b-config");
      setConfig(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchConfig(); }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put("/admin/b2b-config", config);
      toast.success("B2B pricing config saved!");
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  const resetConfig = async () => {
    if (!window.confirm("Reset all B2B pricing to defaults?")) return;
    setResetting(true);
    try {
      await api.post("/admin/b2b-config/reset");
      toast.success("Config reset to defaults");
      fetchConfig();
    } catch {
      toast.error("Failed to reset");
    }
    setResetting(false);
  };

  const updateTier = (tierKey, field, value) => {
    setConfig(prev => ({
      ...prev,
      tiers: { ...prev.tiers, [tierKey]: { ...prev.tiers[tierKey], [field]: field === "label" ? value : Number(value) } }
    }));
  };

  const updateSlab = (index, tier, value) => {
    setConfig(prev => {
      const slabs = [...prev.volume_slabs];
      slabs[index] = { ...slabs[index], prices: { ...slabs[index].prices, [tier]: Number(value) } };
      return { ...prev, volume_slabs: slabs };
    });
  };

  const updateSlabLabel = (index, value) => {
    setConfig(prev => {
      const slabs = [...prev.volume_slabs];
      slabs[index] = { ...slabs[index], label: value };
      return { ...prev, volume_slabs: slabs };
    });
  };

  const updateAddon = (key, field, value) => {
    setConfig(prev => ({
      ...prev,
      addons: { ...prev.addons, [key]: { ...prev.addons[key], [field]: field === "label" ? value : Number(value) } }
    }));
  };

  const updateCities = (tier, value) => {
    const cities = value.split(",").map(c => c.trim().toLowerCase()).filter(Boolean);
    setConfig(prev => ({ ...prev, cities: { ...prev.cities, [tier]: cities } }));
  };

  const updateCostSplit = (key, field, value) => {
    setConfig(prev => ({
      ...prev,
      cost_split: {
        ...prev.cost_split,
        [key]: { ...prev.cost_split?.[key], [field]: field === "label" ? value : Number(value) }
      }
    }));
  };

  // Compute live profit preview
  const costSplit = config?.cost_split || {};
  const totalCostPerItem = Object.values(costSplit).reduce((sum, c) => sum + (c.amount || 0), 0);
  const [previewTier, setPreviewTier] = useState("tier_2");
  const [previewSlab, setPreviewSlab] = useState(0);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div>;

  if (!config) return <p className="text-center text-slate-500 py-20">Failed to load config</p>;

  return (
    <div className="space-y-6" data-testid="admin-b2b-pricing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fadeInUp">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">B2B Pricing Config</h1>
          <p className="text-sm text-slate-500 mt-1">Control all B2B Express pricing, tiers, cities, add-ons & SLA settings</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={resetConfig} disabled={resetting} className="rounded-xl text-sm gap-1.5" data-testid="reset-btn">
            {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Reset Defaults
          </Button>
          <Button onClick={saveConfig} disabled={saving} className="bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl text-sm gap-1.5" data-testid="save-config-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save All Changes
          </Button>
        </div>
      </div>

      {/* Tier Pricing */}
      <Section title="Tier Pricing (Base + Per-kg)" icon={IndianRupee} testId="section-tiers">
        <div className="grid md:grid-cols-3 gap-5">
          {Object.entries(config.tiers || {}).map(([key, tier]) => (
            <div key={key} className="border border-slate-200 rounded-xl p-4 space-y-3">
              <div>
                <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Label</Label>
                <Input value={tier.label} onChange={(e) => updateTier(key, "label", e.target.value)} className="rounded-lg mt-1 h-9 text-sm" data-testid={`tier-label-${key}`} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 uppercase">Base (₹)</Label>
                  <Input type="number" value={tier.base_charge} onChange={(e) => updateTier(key, "base_charge", e.target.value)} className="rounded-lg mt-1 h-9 text-sm" data-testid={`tier-base-${key}`} />
                </div>
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 uppercase">Per-kg (₹)</Label>
                  <Input type="number" value={tier.per_kg_rate} onChange={(e) => updateTier(key, "per_kg_rate", e.target.value)} className="rounded-lg mt-1 h-9 text-sm" data-testid={`tier-perkg-${key}`} />
                </div>
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 uppercase">SLA (days)</Label>
                  <Input type="number" value={tier.sla_days} onChange={(e) => updateTier(key, "sla_days", e.target.value)} className="rounded-lg mt-1 h-9 text-sm" data-testid={`tier-sla-${key}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* City Classification */}
      <Section title="City Tier Classification" icon={MapPin} testId="section-cities">
        <p className="text-xs text-slate-400 mb-4">Comma-separated city names. Cities not listed in Tier 1 or 2 auto-fall to Tier 3.</p>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-600">Tier 1 — Metro Cities</Label>
            <textarea
              value={(config.cities?.tier_1 || []).join(", ")}
              onChange={(e) => updateCities("tier_1", e.target.value)}
              className="w-full mt-1 rounded-xl border border-slate-200 px-4 py-3 text-sm resize-none h-20 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20 focus:outline-none focus:ring-2"
              data-testid="cities-tier1"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Tier 2 — Major Cities</Label>
            <textarea
              value={(config.cities?.tier_2 || []).join(", ")}
              onChange={(e) => updateCities("tier_2", e.target.value)}
              className="w-full mt-1 rounded-xl border border-slate-200 px-4 py-3 text-sm resize-none h-24 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20 focus:outline-none focus:ring-2"
              data-testid="cities-tier2"
            />
          </div>
          <p className="text-xs text-slate-400">Tier 3 = all other cities (auto)</p>
        </div>
      </Section>

      {/* Volume Slab Discounts */}
      <Section title="Volume Slab Discounts" icon={Tag} testId="section-slabs">
        <p className="text-xs text-slate-400 mb-4">Price per 3kg shipment at each volume level.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-2.5 px-3 text-[10px] font-bold uppercase text-slate-400">Slab Label</th>
                <th className="text-left py-2.5 px-3 text-[10px] font-bold uppercase text-slate-400">Min</th>
                <th className="text-left py-2.5 px-3 text-[10px] font-bold uppercase text-slate-400">Max</th>
                <th className="text-center py-2.5 px-3 text-[10px] font-bold uppercase text-slate-400">Tier 1 (₹)</th>
                <th className="text-center py-2.5 px-3 text-[10px] font-bold uppercase text-slate-400">Tier 2 (₹)</th>
                <th className="text-center py-2.5 px-3 text-[10px] font-bold uppercase text-slate-400">Tier 3 (₹)</th>
              </tr>
            </thead>
            <tbody>
              {(config.volume_slabs || []).map((slab, i) => (
                <tr key={`vslab-${slab.min}-${slab.max}`} className="border-t border-slate-100">
                  <td className="py-2 px-3">
                    <Input value={slab.label} onChange={(e) => updateSlabLabel(i, e.target.value)} className="rounded-lg h-8 text-xs w-40" data-testid={`slab-label-${i}`} />
                  </td>
                  <td className="py-2 px-3 text-xs text-slate-500">{slab.min}</td>
                  <td className="py-2 px-3 text-xs text-slate-500">{slab.max >= 999999 ? "∞" : slab.max}</td>
                  <td className="py-2 px-3">
                    <Input type="number" value={slab.prices?.tier_1 || 0} onChange={(e) => updateSlab(i, "tier_1", e.target.value)} className="rounded-lg h-8 text-xs w-20 mx-auto text-center" data-testid={`slab-t1-${i}`} />
                  </td>
                  <td className="py-2 px-3">
                    <Input type="number" value={slab.prices?.tier_2 || 0} onChange={(e) => updateSlab(i, "tier_2", e.target.value)} className="rounded-lg h-8 text-xs w-20 mx-auto text-center" data-testid={`slab-t2-${i}`} />
                  </td>
                  <td className="py-2 px-3">
                    <Input type="number" value={slab.prices?.tier_3 || 0} onChange={(e) => updateSlab(i, "tier_3", e.target.value)} className="rounded-lg h-8 text-xs w-20 mx-auto text-center" data-testid={`slab-t3-${i}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Add-on Services */}
      <Section title="Add-on Services" icon={Package} testId="section-addons">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(config.addons || {}).map(([key, addon]) => (
            <div key={key} className="border border-slate-200 rounded-xl p-4 space-y-2">
              <div>
                <Label className="text-[10px] font-bold text-slate-400 uppercase">Label</Label>
                <Input value={addon.label} onChange={(e) => updateAddon(key, "label", e.target.value)} className="rounded-lg mt-1 h-8 text-sm" data-testid={`addon-label-${key}`} />
              </div>
              {addon.price !== undefined && (
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 uppercase">Price (₹)</Label>
                  <Input type="number" value={addon.price} onChange={(e) => updateAddon(key, "price", e.target.value)} className="rounded-lg mt-1 h-8 text-sm" data-testid={`addon-price-${key}`} />
                </div>
              )}
              {addon.rate_pct !== undefined && (
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 uppercase">Rate (%)</Label>
                  <Input type="number" step="0.1" value={addon.rate_pct} onChange={(e) => updateAddon(key, "rate_pct", e.target.value)} className="rounded-lg mt-1 h-8 text-sm" data-testid={`addon-rate-${key}`} />
                </div>
              )}
              {addon.surcharge !== undefined && (
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 uppercase">Surcharge (₹)</Label>
                  <Input type="number" value={addon.surcharge} onChange={(e) => updateAddon(key, "surcharge", e.target.value)} className="rounded-lg mt-1 h-8 text-sm" data-testid={`addon-surcharge-${key}`} />
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* SLA & GST */}
      <Section title="SLA & Tax Settings" icon={Shield} testId="section-sla-gst">
        <div className="grid sm:grid-cols-2 gap-5 max-w-lg">
          <div>
            <Label className="text-xs font-semibold text-slate-600">SLA Penalty Credit (₹)</Label>
            <Input
              type="number"
              value={config.sla_penalty_credit || 50}
              onChange={(e) => setConfig({ ...config, sla_penalty_credit: Number(e.target.value) })}
              className="rounded-xl mt-1 h-10"
              data-testid="sla-penalty-input"
            />
            <p className="text-[10px] text-slate-400 mt-1">Credited to customer if SLA is missed</p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">GST Rate (%)</Label>
            <Input
              type="number"
              step="0.5"
              value={config.gst_pct || 18}
              onChange={(e) => setConfig({ ...config, gst_pct: Number(e.target.value) })}
              className="rounded-xl mt-1 h-10"
              data-testid="gst-input"
            />
            <p className="text-[10px] text-slate-400 mt-1">Applied on (base + addons)</p>
          </div>
        </div>
      </Section>

      {/* Cost Distribution */}
      <Section title="Cost Distribution (Per Shipment)" icon={Truck} testId="section-cost-split">
        <p className="text-xs text-slate-400 mb-4">Fixed cost per item/shipment. Your profit = Selling Price - Total Ops Cost.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {Object.entries(config.cost_split || {}).map(([key, cost]) => (
            <div key={key} className="border border-slate-200 rounded-xl p-4 space-y-2">
              <div>
                <Label className="text-[10px] font-bold text-slate-400 uppercase">Role</Label>
                <Input value={cost.label || key} onChange={(e) => updateCostSplit(key, "label", e.target.value)} className="rounded-lg mt-1 h-8 text-sm" data-testid={`cost-label-${key}`} />
              </div>
              <div>
                <Label className="text-[10px] font-bold text-slate-400 uppercase">Amount (₹/item)</Label>
                <Input type="number" value={cost.amount || 0} onChange={(e) => updateCostSplit(key, "amount", e.target.value)} className="rounded-lg mt-1 h-8 text-sm" data-testid={`cost-amount-${key}`} />
              </div>
            </div>
          ))}
        </div>
        {/* Live Profit Preview — Dynamic */}
        <div className="rounded-xl border-2 border-slate-200 bg-white p-5 mt-2" data-testid="profit-preview">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Live Profit Preview</h3>
            <div className="flex items-center gap-2">
              <select value={previewTier} onChange={(e) => setPreviewTier(e.target.value)} className="rounded-lg border border-slate-200 text-xs px-3 py-1.5 focus:border-[#FF6B00] focus:outline-none" data-testid="preview-tier-select">
                {Object.entries(config.tiers || {}).map(([k, t]) => (
                  <option key={k} value={k}>{t.label}</option>
                ))}
              </select>
              <select value={previewSlab} onChange={(e) => setPreviewSlab(Number(e.target.value))} className="rounded-lg border border-slate-200 text-xs px-3 py-1.5 focus:border-[#FF6B00] focus:outline-none" data-testid="preview-slab-select">
                {(config.volume_slabs || []).map((s, i) => (
                  <option key={`ps-${s.label}-${s.min}`} value={i}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>
          {(() => {
            const slab = config.volume_slabs?.[previewSlab];
            const sellingPrice = slab?.prices?.[previewTier] || 0;
            const profit = sellingPrice - totalCostPerItem;
            const margin = sellingPrice > 0 ? ((profit / sellingPrice) * 100).toFixed(1) : 0;
            const isPositive = profit >= 0;
            return (
              <>
                <div className={`rounded-xl p-4 ${isPositive ? "bg-emerald-50/60 border border-emerald-200" : "bg-red-50/60 border border-red-200"}`}>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase">Selling Price</p>
                      <p className="font-['Outfit'] text-xl font-bold text-slate-900">₹{sellingPrice}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase">Total Ops Cost</p>
                      <p className="font-['Outfit'] text-xl font-bold text-red-600">-₹{totalCostPerItem}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase">Your Profit</p>
                      <p className={`font-['Outfit'] text-xl font-bold ${isPositive ? "text-emerald-600" : "text-red-600"}`}>₹{profit}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase">Margin</p>
                      <p className={`font-['Outfit'] text-xl font-bold ${isPositive ? "text-emerald-600" : "text-red-600"}`}>{margin}%</p>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.entries(costSplit).map(([key, cost]) => (
                    <span key={key} className="text-[10px] bg-slate-50 border border-slate-200 px-2 py-1 rounded-lg text-slate-600">
                      {cost.label}: <strong>₹{cost.amount}</strong>
                    </span>
                  ))}
                </div>
                {!isPositive && (
                  <p className="mt-3 text-xs text-red-600 font-semibold">Warning: You're losing money at this tier/slab! Increase selling price or reduce ops costs.</p>
                )}
              </>
            );
          })()}
        </div>
      </Section>

      {/* Floating Save Button */}
      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={saveConfig} disabled={saving} className="bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl h-11 px-6 shadow-lg shadow-[#FF6B00]/20 gap-2" data-testid="save-floating-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save All Changes
        </Button>
      </div>
    </div>
  );
}
